import { safeJsonLd } from "@/lib/structured-data";
import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { sanitizeHtml } from "@/lib/sanitize-html";
import { normalizeBlogTypography } from "@/lib/blog-typography";
import { demoteHeadings, rewriteInternalLinks } from "@/lib/blog-internal-links";
import { localizeExternalImages } from "@/lib/blog-external-images";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { NotFoundPage } from "@/components/site/NotFoundPage";
import { getBlogPost } from "@/lib/blog.functions";
import { legacyResultsRedirect } from "@/lib/legacy-results-slugs";
import { legacyPageRedirect } from "@/lib/legacy-page-redirects";
import { withQuery } from "@/lib/redirect-query";
import { brandedTitle, htmlToText, snippet } from "@/lib/meta-text";
import { blogAuthorNode, isPersonAuthor, ORGANIZATION_ID } from "@/lib/blog-schema";
import { absoluteUrl } from "@/lib/absolute-url";

const postQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["blog-post", slug],
    queryFn: () => getBlogPost({ data: { slug } }),
  });

export const Route = createFileRoute("/$slug")({
  loader: async ({ context, params, location }) => {
    // Legacy Cyrillic-homoglyph URL for the LaserCap article (lаѕеrcap-trеаtmеnt
    // with Cyrillic а/ѕ/е lookalikes) — 301 to the corrected slug.
    if (params.slug === "lаѕеrcap-trеаtmеnt") {
      throw redirect({ href: withQuery("/lasercap-treatment/", location.searchStr), statusCode: 301 });
    }
    // Retired results-image posts — 301 to the real results content.
    const legacy = legacyResultsRedirect(params.slug);
    if (legacy) throw redirect({ href: withQuery(legacy, location.searchStr), statusCode: 301 });
    // Retired WordPress page slugs and clone-of-* duplicates still linked from
    // blog posts. Keep the query string so ad and UTM tags survive.
    const page = legacyPageRedirect(params.slug);
    if (page) throw redirect({ href: withQuery(page, location.searchStr), statusCode: 301 });
    const trace = (globalThis as { __rhMark?: (name: string) => void }).__rhMark;
    trace?.("loader.post");
    const post = await context.queryClient.ensureQueryData(postQueryOptions(params.slug));
    trace?.("loader.done");
    if (!post) throw notFound();
    // Legacy URLs (e.g. with Cyrillic lookalike letters) resolve to the post
    // but should land on the canonical slug.
    if (post.slug !== params.slug) {
      throw redirect({ href: withQuery(`/${post.slug}/`, location.searchStr), statusCode: 301 });
    }
    return post;
  },
  head: ({ loaderData }) => {
    (globalThis as { __rhMark?: (name: string) => void }).__rhMark?.("head.slug");
    if (!loaderData)
      return {
        meta: [
          { title: "Page Not Found | RESTORE Hair" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    const title = loaderData.meta_title || loaderData.title;
    // Use the first of meta_description, excerpt and post body that has at
    // least 50 characters, cut at a word boundary. Shorter values are
    // placeholders from the WordPress import (for example "Trending News").
    const candidates = [
      (loaderData.meta_description ?? "").trim(),
      snippet(loaderData.excerpt || ""),
      snippet(htmlToText(loaderData.content_html || "")),
    ];
    const description = candidates.find((c) => c.length >= 50) ?? candidates.find(Boolean) ?? "";
    const ogImage = loaderData.og_image_url || loaderData.featured_image_url;
    return {
      meta: [
        // Drop the brand suffix when it would push the title past 60 characters.
        { title: brandedTitle(title, "RESTORE Hair") },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        ...(ogImage ? [
          { property: "og:image", content: absoluteUrl(ogImage) },
          { name: "twitter:image", content: absoluteUrl(ogImage) },
        ] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: `/${loaderData.slug}/` }],
      scripts: [
        {
          type: "application/ld+json",
          children: safeJsonLd({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: loaderData.title,
            image: ogImage ? [absoluteUrl(ogImage)] : undefined,
            description,
            // A Person only when blog_authors names a real person. Otherwise
            // the post is credited to the organization. No reviewedBy until
            // RESTORE names the doctors who really review posts.
            author: blogAuthorNode(loaderData.author),
            publisher: { "@id": ORGANIZATION_ID },
            datePublished: loaderData.published_at ?? undefined,
            dateModified: loaderData.updated_at ?? loaderData.published_at ?? undefined,
            mainEntityOfPage: { "@type": "WebPage", "@id": `https://restorehair.com/${loaderData.slug}/` },
          }),
        },
      ],
    };
  },
  component: BlogPost,
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-4xl px-4 py-20 text-center">
      <h1 className="text-2xl font-semibold text-foreground">Couldn't load this post</h1>
      <p className="mt-2 text-sm text-muted-foreground">Something went wrong. Please try again.</p>
      <button onClick={reset} className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">Retry</button>
    </div>
  ),
  notFoundComponent: () => <NotFoundPage />,
});

function BlogPost() {
  const { slug } = Route.useParams();
  (globalThis as { __rhMark?: (name: string) => void }).__rhMark?.("react.post");
  const { data: post } = useSuspenseQuery(postQueryOptions(slug));
  (globalThis as { __rhMark?: (name: string) => void }).__rhMark?.("react.post.data");
  if (!post) return null;

  return (
    <>
      <Header />
      <article className="mx-auto max-w-3xl px-4 py-12 lg:py-16">
        <nav className="mb-6 text-sm">
          <Link to="/hair-loss-blog/" className="text-muted-foreground hover:text-primary">← All posts</Link>
        </nav>
        {post.featured_image_url ? (
          <header className="mb-8">
            <div className="relative overflow-hidden rounded-lg bg-foreground">
              <img
                src={post.featured_image_url}
                alt={`${post.title} featured image`}
                className="w-full"
              />
              <h1 className="absolute inset-y-0 left-0 flex w-[46%] items-center px-5 text-xl font-bold leading-tight text-background sm:px-8 sm:text-3xl lg:text-4xl">
                {post.title}
              </h1>
            </div>
          </header>
        ) : (
          <header className="mb-8">
            <h1 className="text-3xl font-bold leading-tight text-foreground lg:text-4xl">{post.title}</h1>
          </header>
        )}
        <PostByline author={post.author} publishedAt={post.published_at} />
        <div
          className="blog-post-content"
          dangerouslySetInnerHTML={{
            __html: normalizeBlogTypography(demoteHeadings(rewriteInternalLinks(localizeExternalImages(sanitizeHtml(post.content_html))))),
          }}
        />
      </article>
      <Footer />
    </>
  );
}
// The visible author and date. The BlogPosting JSON-LD names the same author,
// and search engines expect the credited author to be visible on the page.
// The date uses UTC, so the server and the browser render the same text.
function PostByline({
  author,
  publishedAt,
}: {
  author: Parameters<typeof isPersonAuthor>[0];
  publishedAt: string | null | undefined;
}) {
  const date = publishedAt
    ? new Date(publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" })
    : null;
  return (
    <p className="mb-8 text-sm text-muted-foreground">
      By{" "}
      {isPersonAuthor(author) ? (
        <a href={`/author/${author.slug}/`} className="font-semibold text-foreground hover:text-primary">
          {author.name.trim()}
        </a>
      ) : (
        <span className="font-semibold text-foreground">RESTORE Hair</span>
      )}
      {date && publishedAt ? (
        <>
          {" · "}
          <time dateTime={publishedAt}>{date}</time>
        </>
      ) : null}
    </p>
  );
}
